export const trustedCourseIframeDomains = [
	"airtable.com",
	"articulate.com",
	"articulateusercontent.com",
	"bubbleapps.io",
	"calendly.com",
	"canva.com",
	"cloud.scorm.com",
	"docs.google.com",
	"figma.com",
	"fillout.com",
	"forms.office.com",
	"genially.com",
	"github.io",
	"glide.page",
	"h5p.com",
	"jotform.com",
	"miro.com",
	"netlify.app",
	"padlet.com",
	"pages.dev",
	"paperform.co",
	"replit.app",
	"softr.app",
	"streamlit.app",
	"tally.so",
	"typeform.com",
	"vercel.app",
	"vimeo.com",
	"youtube.com",
	"youtube-nocookie.com",
] as const

export function normalizeCourseIframeUrl(value: string): string | null {
	if (value.length > 2048) return null

	try {
		const url = new URL(value.trim())
		if (url.protocol !== "https:" || url.username !== "" || url.password !== "") return null
		if (url.port !== "" && url.port !== "443") return null
		if (!isPublicHostname(url.hostname)) return null
		url.hash = ""
		return url.toString()
	} catch {
		return null
	}
}

export function getCourseIframeHostname(url: string): string | null {
	const normalizedUrl = normalizeCourseIframeUrl(url)
	return normalizedUrl === null ? null : new URL(normalizedUrl).hostname.toLowerCase()
}

export function isCourseIframeDomainAllowed(url: string, additionalDomains: readonly string[] = []): boolean {
	const hostname = getCourseIframeHostname(url)
	if (hostname === null) return false
	return [...trustedCourseIframeDomains, ...additionalDomains].some(domain => matchesDomain(hostname, domain))
}

export function isCourseIframeApplicationOrigin(url: string, applicationOrigins: readonly string[]): boolean {
	const normalizedUrl = normalizeCourseIframeUrl(url)
	if (normalizedUrl === null) return false

	const origin = new URL(normalizedUrl).origin
	return applicationOrigins.some(applicationOrigin => normalizeOrigin(applicationOrigin) === origin)
}

export function isCourseIframeUrlAllowed(
	url: string,
	additionalDomains: readonly string[],
	applicationOrigins: readonly string[],
): boolean {
	return (
		isCourseIframeDomainAllowed(url, additionalDomains) && !isCourseIframeApplicationOrigin(url, applicationOrigins)
	)
}

function matchesDomain(hostname: string, domain: string): boolean {
	const normalizedDomain = domain
		.trim()
		.toLowerCase()
		.replace(/^\.+|\.+$/g, "")
	return normalizedDomain !== "" && (hostname === normalizedDomain || hostname.endsWith(`.${normalizedDomain}`))
}

function normalizeOrigin(value: string): string | null {
	try {
		return new URL(value).origin
	} catch {
		return null
	}
}

function isPublicHostname(hostname: string): boolean {
	const normalizedHostname = hostname.toLowerCase().replace(/^\[|\]$|\.$/g, "")
	if (
		normalizedHostname === "localhost" ||
		normalizedHostname.endsWith(".localhost") ||
		normalizedHostname.endsWith(".local") ||
		normalizedHostname.endsWith(".internal")
	) {
		return false
	}

	const ipv4Parts = normalizedHostname.split(".").map(Number)
	if (ipv4Parts.length === 4 && ipv4Parts.every(part => Number.isInteger(part) && part >= 0 && part <= 255)) {
		const [first, second, third] = ipv4Parts
		return !(
			first === 0 ||
			first === 10 ||
			(first === 100 && second >= 64 && second <= 127) ||
			first === 127 ||
			(first === 169 && second === 254) ||
			(first === 172 && second >= 16 && second <= 31) ||
			(first === 192 && second === 0 && third === 0) ||
			(first === 192 && second === 0 && third === 2) ||
			(first === 192 && second === 88 && third === 99) ||
			(first === 192 && second === 168) ||
			(first === 198 && (second === 18 || second === 19)) ||
			(first === 198 && second === 51 && third === 100) ||
			(first === 203 && second === 0 && third === 113) ||
			first >= 224
		)
	}

	if (normalizedHostname.includes(":")) {
		return !(
			normalizedHostname === "::" ||
			normalizedHostname === "::1" ||
			normalizedHostname.startsWith("fc") ||
			normalizedHostname.startsWith("fd") ||
			normalizedHostname.startsWith("fe8") ||
			normalizedHostname.startsWith("fe9") ||
			normalizedHostname.startsWith("fea") ||
			normalizedHostname.startsWith("feb") ||
			normalizedHostname.startsWith("::ffff:")
		)
	}

	return normalizedHostname.includes(".")
}
